import { type ReactNode, type CSSProperties } from 'react';
import cl from 'clsx';

type TableColumnDescription<Key extends string> = {
  key: Key;
  width: number;
};

type Padding = [className: string, extraWidth: number];

const TABLE_COLUMN_PADDING: Padding = ['px-1', 4 + 4];
const TABLE_COLUMN_PADDING_FIRST: Padding = ['pl-[7px] pr-1', 7 + 4];
const TABLE_COLUMN_PADDING_LAST: Padding = ['pl-1 pr-0', 4 + 0];

const makeTableColumnsLayout = <Key extends string>(
  tableColumnsOrder: readonly TableColumnDescription<Key>[],
) => {
  const processItem = (
    input: TableColumnDescription<Key>,
    className: string,
    extraWidth: number,
  ) => {
    const width = input.width + extraWidth;

    return {
      key: input.key,
      className: cl(className),
      style: {
        width,
        minWidth: width,
        maxWidth: width,
      },
    };
  };

  const first = tableColumnsOrder[0];
  const last = tableColumnsOrder[tableColumnsOrder.length - 1];

  const result: {
    key: Key;
    style: CSSProperties;
    className: string;
  }[] = [
    processItem(first, ...TABLE_COLUMN_PADDING_FIRST),
    ...tableColumnsOrder
      .slice(1, -1)
      .map((column) => processItem(column, ...TABLE_COLUMN_PADDING)),
    processItem(last, ...TABLE_COLUMN_PADDING_LAST),
  ];

  return result;
};

const TABLE_COLUMNS_SPEC = [
  //from figma
  { key: 'ordinal', width: 27 },
  { key: 'photo_url', width: 26 },
  { key: 'full_name', width: 120 },
  { key: 'comment', width: 141 },
  { key: 'grantedAt', width: 80 },
  { key: 'admin', width: 55 },
  { key: 'pulse', width: 180 },
  { key: 'audience', width: 64 },
  { key: 'invite_link', width: 113 },
] as const;

export const TABLE_COLUMNS_LAYOUT = makeTableColumnsLayout(TABLE_COLUMNS_SPEC);

export type TableColumnKey = (typeof TABLE_COLUMNS_SPEC)[number]['key'];

export type TableReactNodeRecord = Record<TableColumnKey, ReactNode>;

export const FILLER_STYLE = { width: '100%' };
